import React from "react";
import * as Sentry from "@sentry/react";

/**
 * ErrorBoundary — Composant global de gestion d'erreurs React
 *
 * Attrape toute erreur de rendu dans l'arbre enfant et affiche
 * un écran de secours au lieu du crash (écran blanc).
 * Utilisé comme wrapper dans App.js.
 *
 * Props :
 *   - children : arbre React à protéger
 *   - fallback : (optionnel) composant custom de fallback
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  /* Déclenché au moment du rendu si une erreur survient dans un enfant */
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  /* Log de l'erreur — prêt pour Sentry quand on l'intègre */
  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary] Erreur capturée :", error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });
  }

  /* Recharge la page pour réinitialiser l'état */
  handleReload = () => {
    window.location.reload();
  };

  /* Retour à l'accueil (sous-domaine actuel) */
  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      /* Si un fallback custom est fourni, l'utiliser */
      if (this.props.fallback) {
        return this.props.fallback;
      }

      /* Écran de secours par défaut */
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            {/* Icône erreur */}
            <div style={styles.iconWrapper}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#E74C3C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <h1 style={styles.title}>Une erreur est survenue</h1>
            <p style={styles.message}>
              L'application a rencontré un problème inattendu.
              Veuillez réessayer ou contacter le support si le problème persiste.
            </p>

            {/* Détail technique (dev only, masqué en prod si besoin) */}
            {this.state.error && (
              <details style={styles.details}>
                <summary style={styles.summary}>Détails techniques</summary>
                <pre style={styles.pre}>
                  {this.state.error.toString()}
                </pre>
              </details>
            )}

            {/* Actions */}
            <div style={styles.actions}>
              <button onClick={this.handleReload} style={styles.btnPrimary}>
                Recharger la page
              </button>
              <button onClick={this.handleGoHome} style={styles.btnSecondary}>
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/* ───────────────────────────────────────────────
   Styles inline — cohérents avec le thème Bati Viz
   (couleurs reprises de theme.js)
   ─────────────────────────────────────────────── */
const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#F4F6FA",
    padding: "24px",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: "12px",
    padding: "48px 40px",
    maxWidth: "480px",
    width: "100%",
    textAlign: "center",
    boxShadow: "0 2px 16px rgba(13,27,42,0.08)",
  },
  iconWrapper: {
    marginBottom: "20px",
  },
  title: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#0D1B2A",
    margin: "0 0 12px 0",
  },
  message: {
    fontSize: "14px",
    color: "#6B7280",
    lineHeight: "1.6",
    margin: "0 0 24px 0",
  },
  details: {
    textAlign: "left",
    marginBottom: "24px",
    background: "#F9FAFB",
    borderRadius: "8px",
    padding: "12px",
    border: "1px solid #E5E7EB",
  },
  summary: {
    fontSize: "13px",
    color: "#6B7280",
    cursor: "pointer",
    fontWeight: 500,
  },
  pre: {
    fontSize: "12px",
    color: "#E74C3C",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    marginTop: "8px",
    marginBottom: 0,
    fontFamily: "'JetBrains Mono', monospace",
  },
  actions: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  btnPrimary: {
    padding: "10px 24px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#fff",
    background: "#2563EB",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  btnSecondary: {
    padding: "10px 24px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#374151",
    background: "#F3F4F6",
    border: "1px solid #D1D5DB",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background 0.2s",
  },
};

export default ErrorBoundary;
